import { css } from '@styled-system/css';
import { Hourglass, XCircle } from 'lucide-react';
import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AnalysisComputeState } from '@/shared/api/types/generated';
import { Button } from '@/shared/components/ui/Button';

// ============================================================================
// Création asynchrone (changelog 2026-08-05) : écran d'attente pendant que le
// calcul serveur tourne (1 à 2 min). L'API ne donne aucun avancement réel sur
// la plupart des analyses (publishedCount saute directement à la fin), la
// barre est donc SIMULÉE : elle suit le temps écoulé depuis la création selon
// une courbe asymptotique (rapide au début, plafonnée à 93 %), et se remplit
// à 100 % quand le résultat arrive (prop `done`), juste avant la bascule.
// ============================================================================

const wrap = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '5',
  paddingY: '20',
  paddingX: '6',
  textAlign: 'center',
});

const iconCircle = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '56px',
  height: '56px',
  borderRadius: 'full',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  color: 'text.secondary',
});

const title = css({ fontSize: 'lg', fontWeight: 'semibold', color: 'text.primary' });

const hint = css({ fontSize: 'bodySm', color: 'text.tertiary', maxWidth: '420px', lineHeight: 1.6 });

const progressWrap = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '2.5',
  width: '100%',
  maxWidth: '360px',
});

const progressTrack = css({
  width: '100%',
  height: '7px',
  borderRadius: 'full',
  bg: 'surface.sunken',
  overflow: 'hidden',
});

const progressFill = css({
  height: '100%',
  borderRadius: 'full',
  bg: 'brand.primary',
  transition: 'width 600ms cubic-bezier(0.25, 1, 0.5, 1)',
});

const progressLabel = css({
  fontFamily: 'mono',
  fontSize: 'caption',
  fontWeight: 'semibold',
  color: 'text.secondary',
  alignSelf: 'flex-end',
});

const publishedBadge = css({
  fontFamily: 'mono',
  fontSize: 'caption',
  fontWeight: 'semibold',
  color: 'brand.primary',
});

/**
 * Progression simulée depuis la création : 1 - e^(-t/45 s), plafonnée à 93 %
 * (≈ 49 % à 30 s, 74 % à 60 s, 86 % à 90 s). En file d'attente, la barre
 * reste sous 6 % : le calcul n'a pas commencé.
 */
const simulatedProgress = (createdAt: Date | undefined, state: AnalysisComputeState): number => {
  if (!createdAt) return 0.03;
  const elapsedSec = Math.max(0, (Date.now() - new Date(createdAt).getTime()) / 1000);
  if (state === 'queued') return Math.min(0.06, 0.02 + elapsedSec / 600);
  return Math.min(0.93, 1 - Math.exp(-elapsedSec / 45));
};

export interface AnalysisComputePendingProps {
  state: AnalysisComputeState;
  /** Résultat arrivé : la barre se remplit à 100 % avant la bascule. */
  done?: boolean;
  createdAt?: Date;
  queuePosition?: number | null;
  publishedCount?: number;
  computeError?: string | null;
  /** Supprime l'analyse (annule aussi le calcul serveur) et revient à la liste. */
  onCancel?: () => void;
}

export const AnalysisComputePending: FC<AnalysisComputePendingProps> = ({
  state,
  done = false,
  createdAt,
  queuePosition,
  publishedCount = 0,
  computeError,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(() => simulatedProgress(createdAt, state));

  useEffect(() => {
    if (done) {
      setProgress(1);
      return;
    }
    const update = () => setProgress(simulatedProgress(createdAt, state));
    update();
    const handle = window.setInterval(update, 500);
    return () => window.clearInterval(handle);
  }, [done, createdAt, state]);

  if (state === 'failed') {
    return (
      <div className={wrap}>
        <span className={css({ color: 'error.default' })}>
          <XCircle size={44} strokeWidth={1.5} />
        </span>
        <span className={title}>{t('analysisDetail.computeFailed')}</span>
        <span className={hint}>{computeError || t('analysisDetail.computeFailedHint')}</span>
        {onCancel && (
          <Button variant="danger" size="md" handleClick={onCancel} showLoader>
            {t('analysisDetail.computeDelete')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={wrap}>
      {state === 'queued' && !done && (
        <span className={iconCircle}>
          <Hourglass size={22} strokeWidth={1.5} />
        </span>
      )}
      <span className={title}>
        {done
          ? t('analysisDetail.computeDone')
          : state === 'queued'
            ? t('analysisDetail.computeQueued')
            : t('analysisDetail.computeRunning')}
      </span>
      <span className={hint}>
        {done
          ? t('analysisDetail.computeDoneHint')
          : state === 'queued' && queuePosition != null
            ? t('analysisDetail.computeQueuedPosition', { position: queuePosition })
            : t('analysisDetail.computeRunningHint')}
      </span>

      <div className={progressWrap}>
        <div className={progressTrack}>
          <div className={progressFill} style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
        <span className={progressLabel}>{Math.round(progress * 100)} %</span>
      </div>

      {/* Avancement chiffré réel uniquement quand il grimpe (modes fusionné/texte) */}
      {!done && publishedCount > 0 && (
        <span className={publishedBadge}>{t('analysisDetail.computePublished', { count: publishedCount })}</span>
      )}

      {!done && onCancel && (
        <Button variant="secondary" size="md" handleClick={onCancel} showLoader>
          {t('analysisDetail.computeCancel')}
        </Button>
      )}
    </div>
  );
};
